import { ESymbolCategory, ISymbol } from "@kuika/kuika-cl-model";

/**
 * KOA-17362 — CANONICAL symbol factory for workflow DSL bindings.
 *
 * A bound value in Kuika is an `ISymbol`. There are exactly TWO producers of
 * binding symbols for a workflow step: the designer's symbol picker (a user
 * clicking through the cascader) and the workflow DSL (`ui_workflow_dsl`
 * MAP_INPUT / MAP_OUTPUT / LINKED_CONFIG). Every consumer — the action panel,
 * project validation, and each code generator — reads DIFFERENT fields of the
 * same symbol:
 *
 *   • the action panel shows `symbol.description` (symbol-picker.tsx
 *     `getSymbolDescription`) — an empty description renders as an EMPTY cell,
 *     which reads as "the AI left this parameter unbound";
 *   • project validation calls `SymbolModel.IsEmpty()`
 *     (Backend/Kuika.Designtime.Model/ActionModels/SymbolModel.cs) which keys on
 *     a DIFFERENT field per category — `FieldName` for `WorkflowParameter`(25),
 *     `Label` for `Default`(5), `FixedValue` for `Fixed`(4),
 *     `ActionDefinitionId` (+ `FieldName`) for the action-result categories;
 *   • codegen keys on yet another field — `InputName` for process-instance
 *     values (WorkflowStepGenerator `GetParameterTemplate`), `DefaultValueName`
 *     for defaults (`GetDefaultValue`), `ActionDefinitionId` + `FieldName` for
 *     action results (`SetOutputs`).
 *
 * So a symbol that satisfies codegen can still be reported EMPTY by validation
 * and render BLANK in the panel. That is not a hypothetical: the DSL used to
 * hand-build each symbol inline and omitted `fieldName` on process-instance
 * bindings (→ KV1170 "The required parameter is not set" on values that were
 * demonstrably bound and generated correct C#) and omitted `description` on
 * platform-default bindings (→ the parameter looked unbound in the panel).
 *
 * The fix is one source of truth: the DSL never hand-builds a symbol, it calls
 * a builder here. Each builder reproduces the shape
 * `designer-symbol-picker-engine.tsx buildSymbolFromSelectedOptions` produces
 * for the SAME leaf, so a DSL-written binding is indistinguishable from a
 * hand-picked one. `__tests__/workflow-symbol-factory.test.ts` pins that parity
 * field by field, including the emptiness contract each consumer keys on.
 *
 * Scope: BINDING symbols (what a parameter is bound TO). A workflow parameter's
 * own editable `defaultValue` is a different concept (see
 * `workflow-param-default-category.test.ts`) and is not built here.
 */

/** A reference to another workflow parameter of the SAME process. */
export interface IWorkflowParamRefSymbolInput {
  /** Parameter name — what the panel shows and what validation checks. */
  readonly name: string;
  /** Parameter id — what codegen resolves the value through. */
  readonly id: string;
  /** The parameter's own declared type. */
  readonly dataType: string;
  /** The target (bound-to) side's declared type, when it differs from the source. */
  readonly targetType?: string;
}

/** A value the RUNNING PROCESS carries about itself (initiator, instance/step id, MI counters). */
export interface IProcessInstanceSymbolInput {
  /** The FIXED platform keyword codegen switches on (`processInstance`, `processInitiator`, …). */
  readonly inputName: string;
  /** The picker's leaf label — also the `fieldName` validation keys on. */
  readonly label: string;
  /** Backend value type of the instance value (String identities / Int32 MI counters). */
  readonly type: string;
  /** The target (bound-to) side's declared type, when it differs from the source. */
  readonly targetType?: string;
}

/** A step action's RESULT (optionally one field of it) — the source of a step OUTPUT. */
export interface IActionResultSymbolInput {
  /** Action definition id — what `SetOutputs` finds the step's action by, and what validation checks. */
  readonly actionDefinitionId: string;
  /** The action's display name; only ever shown in the panel description. */
  readonly actionDisplayName: string;
  /** Instance id of the action on the step, when known (the picker's `resultActionId`). */
  readonly resultActionId?: string;
  /** Field of the result to extract. Omit for a scalar-returning action (whole result). */
  readonly fieldName?: string;
  /** Type of the extracted value. */
  readonly type?: string;
  /** The target (bound-to) side's declared type, when it differs from the source. */
  readonly targetType?: string;
}

/**
 * Bind to another workflow parameter. Picker parity: the wf-parameter leaves are
 * `processParameter` nodes whose `label` is the parameter NAME and whose
 * `inputName` is the parameter ID, so `fieldName` (validation) carries the name
 * and `inputName` (codegen) carries the id.
 */
export function buildWorkflowParamRefSymbol(input: IWorkflowParamRefSymbolInput): ISymbol {
  const targetType: string = input.targetType ?? input.dataType;
  return {
    category: ESymbolCategory.processParameter,
    label: input.name,
    description: input.name,
    fieldName: input.name,
    inputName: input.id,
    currentType: input.dataType as ISymbol["currentType"],
    targetType: targetType as ISymbol["targetType"],
    fieldType: input.dataType
  };
}

/**
 * Bind to a process-instance value. Picker parity:
 * `designer-symbol-picker-engine.tsx` `case ESymbolCategory.processParameter`
 * sets `fieldName = node.label` — WITHOUT it `SymbolModel.IsEmpty()` reports the
 * binding empty for category 25 and validation raises KV1170 even though
 * codegen (which switches on `inputName`) emits the correct value.
 */
export function buildProcessInstanceSymbol(input: IProcessInstanceSymbolInput): ISymbol {
  const targetType: string = input.targetType ?? input.type;
  return {
    category: ESymbolCategory.processParameter,
    label: input.label,
    description: input.label,
    fieldName: input.label,
    inputName: input.inputName,
    currentType: input.type as ISymbol["currentType"],
    targetType: targetType as ISymbol["targetType"],
    fieldType: input.type
  };
}

/**
 * Bind to a platform-generated value (NewGuid / Now / Empty / CurrentUsername …).
 * Picker parity: `label` is the constant `"Default Value"` (what
 * `IsEmpty()` checks for category 5), `defaultValueName` is the keyword codegen
 * switches on, and `description` is that keyword — the panel shows the
 * description, so leaving it empty makes a bound default LOOK unbound.
 */
export function buildPlatformDefaultSymbol(defaultValueName: string, targetType?: string): ISymbol {
  return {
    category: ESymbolCategory.default,
    label: "Default Value",
    description: defaultValueName,
    defaultValueName,
    currentType: targetType as ISymbol["currentType"],
    targetType: targetType as ISymbol["targetType"]
  };
}

/**
 * Bind to a fixed literal. Picker parity: `label` is the constant
 * `"Fixed Value"`, `isFixed` is set, and the value lives in `fixedValue` (what
 * `IsEmpty()` checks for category 4) while `description` stays empty — the panel
 * renders an editable value input for this category instead of the description
 * (`symbol-picker.tsx`), so a description here would compete with the real value.
 */
export function buildFixedValueSymbol(value: string): ISymbol {
  return {
    category: ESymbolCategory.fixed,
    label: "Fixed Value",
    description: "",
    isFixed: true,
    fixedValue: value
  };
}

/**
 * Bind to a step action's result. Picker parity: selecting an action result
 * yields an `actionResult` symbol carrying `actionDefinitionId`; drilling into a
 * field appends the field to `fieldName` and switches the category to
 * `actionField`, which is what `SetOutputs`' field accessor needs. The panel
 * substitutes the real action name into the `[...]` segment of the description
 * (`symbol-picker.tsx getSymbolDescription`), so the bracket form is required.
 */
export function buildActionResultSymbol(input: IActionResultSymbolInput): ISymbol {
  const hasField: boolean = typeof input.fieldName === "string" && input.fieldName.length > 0;
  const description: string = hasField
    ? `[${input.actionDisplayName}].${input.fieldName}`
    : `[${input.actionDisplayName}]`;
  const symbol: ISymbol = {
    category: hasField ? ESymbolCategory.actionField : ESymbolCategory.actionResult,
    label: "Action Result",
    description,
    actionDefinitionId: input.actionDefinitionId,
    currentType: (input.type ?? "Unknown") as ISymbol["currentType"],
    targetType: (input.targetType ?? input.type ?? "Unknown") as ISymbol["targetType"]
  };
  if (hasField) symbol.fieldName = input.fieldName;
  if (input.resultActionId) symbol.resultActionId = input.resultActionId;
  return symbol;
}
